import gsap from 'gsap';
import { Flip } from 'gsap/Flip';

export class HeroCardsService {
  #IS_ANIMATING = false;
  #LEFT_CARD: HTMLElement | null = null;
  #CENTER_CARD: HTMLElement | null = null;
  #RIGHT_CARD: HTMLElement | null = null;
  #hoverHandlers = new Map<HTMLElement, { enter: () => void; leave: () => void }>();
  #floatingTimelines: gsap.core.Timeline[] = [];
  #cardStates: {
    left: { y: number; rotation: number };
    center: { y: number; rotation: number };
    right: { y: number; rotation: number };
  } = {
    left: { y: 0, rotation: 0 },
    center: { y: 0, rotation: 0 },
    right: { y: 0, rotation: 0 },
  };

  constructor() {
    gsap.registerPlugin(Flip);

    this.#LEFT_CARD = document.getElementById('hero-card-left');
    this.#CENTER_CARD = document.getElementById('hero-card-center');
    this.#RIGHT_CARD = document.getElementById('hero-card-right');

    this.#setInitialState();

    this.#IS_ANIMATING = true;

    setTimeout((): void => {
      this.#revealCards();
    }, 500);

    this.#addClickEventListeners();
    this.#addHoverEffects();
  }

  swapCards(card: HTMLElement): void {
    if (this.#IS_ANIMATING || !this.#LEFT_CARD || !this.#CENTER_CARD || !this.#RIGHT_CARD) {
      return;
    }

    this.#IS_ANIMATING = true;

    const isLeftCard: boolean = card === this.#LEFT_CARD;

    this.#saveCardStates();

    if (isLeftCard) {
      const temp = { ...this.#cardStates.left };
      this.#cardStates.left = { ...this.#cardStates.center };
      this.#cardStates.center = temp;
    } else {
      const temp = { ...this.#cardStates.right };
      this.#cardStates.right = { ...this.#cardStates.center };
      this.#cardStates.center = temp;
    }

    this.#stopFloatingAnimation();

    const thirdCard: HTMLElement | null = isLeftCard ? this.#RIGHT_CARD : this.#LEFT_CARD;

    const container: HTMLElement | null = card.parentElement;
    if (!container) {
      return;
    }

    const containerHeight: number = container.offsetHeight;
    container.style.height = `${containerHeight.toString()}px`;

    const state: Flip.FlipState = Flip.getState([card, this.#CENTER_CARD, thirdCard]);

    const parent: HTMLElement | null = card.parentElement;
    if (!parent) {
      return;
    }

    const cardPlaceholder: HTMLDivElement = document.createElement('div');
    const centerPlaceholder: HTMLDivElement = document.createElement('div');

    parent.insertBefore(cardPlaceholder, card);
    parent.insertBefore(centerPlaceholder, this.#CENTER_CARD);

    card.remove();
    this.#CENTER_CARD.remove();

    parent.insertBefore(card, centerPlaceholder);
    parent.insertBefore(this.#CENTER_CARD, cardPlaceholder);

    cardPlaceholder.remove();
    centerPlaceholder.remove();

    const cardClasses: string = card.className;
    card.className = this.#CENTER_CARD.className;
    this.#CENTER_CARD.className = cardClasses;

    const oldCenter: HTMLElement = this.#CENTER_CARD;

    this.#hideCenterCardInfo(oldCenter);
    this.#removeHoverEffects(oldCenter);

    card.removeEventListener('click', this.#handleCardClick);
    oldCenter.addEventListener('click', this.#handleCardClick);

    this.#CENTER_CARD = card;
    if (isLeftCard) {
      this.#LEFT_CARD = oldCenter;
    } else {
      this.#RIGHT_CARD = oldCenter;
    }

    const oldCenterImg: HTMLImageElement | null = oldCenter.querySelector('img');
    if (oldCenterImg) {
      gsap.set(oldCenterImg, { filter: 'blur(4px)' });
    }

    oldCenter.style.pointerEvents = 'auto';
    oldCenter.style.cursor = 'pointer';

    Flip.from(state, {
      duration: 0.5,
      ease: 'power2.inOut',
      absolute: true,
      onComplete: (): void => {
        container.style.height = '';

        const clickedDataUrl: string = this.#CENTER_CARD!.getAttribute('data-url')!;
        const newCenterCard: HTMLElement = this.#convertToLink(
          this.#CENTER_CARD!,
          clickedDataUrl || '#',
        );

        const sideCard: HTMLElement | null = isLeftCard ? this.#LEFT_CARD : this.#RIGHT_CARD;
        const newSideCard: HTMLElement = this.#convertToDiv(sideCard!);

        this.#CENTER_CARD = newCenterCard;

        if (isLeftCard) {
          this.#LEFT_CARD = newSideCard;
        } else {
          this.#RIGHT_CARD = newSideCard;
        }

        const newCenterImg: HTMLImageElement | null = this.#CENTER_CARD.querySelector('img');
        if (newCenterImg) {
          gsap.set(newCenterImg, { filter: 'none' });
        }

        newSideCard.addEventListener('click', this.#handleCardClick);

        this.#addHoverEffects();
        this.#showCenterCardInfo();
        this.#addFloatingAnimation();
      },
    });

    gsap.to(this.#CENTER_CARD.querySelector('img'), {
      filter: 'none',
      duration: 0.5,
      ease: 'power2.inOut',
      scale: 1,
    });

    gsap.to(oldCenter.querySelector('img'), {
      filter: 'blur(4px)',
      duration: 0.5,
      ease: 'power2.inOut',
    });
  }

  #convertToDiv(element: HTMLElement): HTMLElement {
    const div: HTMLDivElement = document.createElement('div');

    Array.from(element.attributes).forEach((attr) => {
      if (attr.name !== 'href') {
        div.setAttribute(attr.name, attr.value);
      }
    });

    div.innerHTML = element.innerHTML;
    element.parentNode?.replaceChild(div, element);

    return div;
  }

  #convertToLink(element: HTMLElement, url: string): HTMLElement {
    const link: HTMLAnchorElement = document.createElement('a');

    Array.from(element.attributes).forEach((attr) => {
      link.setAttribute(attr.name, attr.value);
    });

    link.href = url;
    link.innerHTML = element.innerHTML;
    element.parentNode?.replaceChild(link, element);

    return link;
  }

  #setInitialState(): void {
    if (this.#LEFT_CARD) {
      gsap.set(this.#LEFT_CARD, { opacity: 0, scale: 0.8, y: 30 });
    }

    if (this.#CENTER_CARD) {
      gsap.set(this.#CENTER_CARD, { opacity: 0, scale: 0.8, y: 30 });
    }

    if (this.#RIGHT_CARD) {
      gsap.set(this.#RIGHT_CARD, { opacity: 0, scale: 0.8, y: 30 });
    }
  }

  #revealCards(): void {
    const tl: GSAPTimeline = gsap.timeline();

    if (this.#CENTER_CARD) {
      tl.to(this.#CENTER_CARD, {
        opacity: 1,
        scale: 1,
        y: 0,
        duration: 0.6,
        ease: 'back.out(1.2)',
        onComplete: (): void => {
          this.#showCenterCardInfo();
        },
      });
    }

    if (this.#LEFT_CARD && this.#RIGHT_CARD) {
      tl.to(
        [this.#LEFT_CARD, this.#RIGHT_CARD],
        {
          opacity: 1,
          scale: 1,
          y: 0,
          duration: 0.5,
          ease: 'back.out(1.2)',
          stagger: 0,
          onComplete: (): void => {
            this.#addFloatingAnimation();
          },
        },
        '-=0.2',
      );
    }
  }

  #addClickEventListeners(): void {
    this.#LEFT_CARD?.addEventListener('click', this.#handleCardClick);
    this.#RIGHT_CARD?.addEventListener('click', this.#handleCardClick);
  }

  #addHoverEffects(): void {
    this.#addHoverStateCenterCard();
    this.#addHoverStateSideCards();
  }

  #addHoverStateCenterCard(): void {
    if (!this.#CENTER_CARD) {
      return;
    }

    const centerCard: HTMLElement = this.#CENTER_CARD;
    const img: HTMLImageElement | null = centerCard.querySelector('img');

    if (!img) {
      return;
    }

    const handleMouseEnter = (): void => {
      gsap.to(img, {
        scale: 1.1,
        duration: 0.35,
        ease: 'power2.out',
      });
    };

    const handleMouseLeave = (): void => {
      gsap.to(img, {
        scale: 1,
        duration: 0.2,
        ease: 'power2.out',
      });
    };

    this.#hoverHandlers.set(centerCard, {
      enter: handleMouseEnter,
      leave: handleMouseLeave,
    });

    centerCard.addEventListener('mouseenter', handleMouseEnter);
    centerCard.addEventListener('mouseleave', handleMouseLeave);
  }

  #addHoverStateSideCards(): void {
    if (!this.#LEFT_CARD || !this.#RIGHT_CARD) {
      return;
    }

    const leftCard: HTMLElement = this.#LEFT_CARD;
    const rightCard: HTMLElement = this.#RIGHT_CARD;
    const leftImg: HTMLImageElement | null = leftCard.querySelector('img');
    const rightImg: HTMLImageElement | null = rightCard.querySelector('img');

    if (!leftImg || !rightImg) {
      return;
    }

    gsap.set([leftImg, rightImg], { filter: 'blur(4px)', scale: 1 });

    const handleMouseEnterLeft = (): void => {
      gsap.to(leftImg, {
        filter: 'none',
        scale: 1.1,
        duration: 0.35,
        ease: 'power2.out',
      });
    };

    const handleMouseEnterRight = (): void => {
      gsap.to(rightImg, {
        filter: 'none',
        scale: 1.1,
        duration: 0.35,
        ease: 'power2.out',
      });
    };

    const handleMouseLeaveLeft = (): void => {
      gsap.to(leftImg, {
        filter: 'blur(4px)',
        scale: 1,
        duration: 0.2,
        ease: 'power2.out',
      });
    };

    const handleMouseLeaveRight = (): void => {
      gsap.to(rightImg, {
        filter: 'blur(4px)',
        scale: 1,
        duration: 0.2,
        ease: 'power2.out',
      });
    };

    this.#hoverHandlers.set(leftCard, {
      enter: handleMouseEnterLeft,
      leave: handleMouseLeaveLeft,
    });
    this.#hoverHandlers.set(rightCard, {
      enter: handleMouseEnterRight,
      leave: handleMouseLeaveRight,
    });

    leftCard.addEventListener('mouseenter', handleMouseEnterLeft);
    leftCard.addEventListener('mouseleave', handleMouseLeaveLeft);
    rightCard.addEventListener('mouseenter', handleMouseEnterRight);
    rightCard.addEventListener('mouseleave', handleMouseLeaveRight);
  }

  #removeHoverEffects(card: HTMLElement): void {
    const handlers = this.#hoverHandlers.get(card);

    if (handlers) {
      card.removeEventListener('mouseenter', handlers.enter);
      card.removeEventListener('mouseleave', handlers.leave);
      this.#hoverHandlers.delete(card);
    }

    const img: HTMLImageElement | null = card.querySelector('img');

    if (img) {
      gsap.killTweensOf(img);
      gsap.set(img, { scale: 1 });
    }
  }

  #showCenterCardInfo(): void {
    if (!this.#CENTER_CARD) {
      return;
    }

    const info: Element | null = this.#CENTER_CARD.querySelector('.hero-card-info');

    if (!info) {
      return;
    }

    gsap.to(info, {
      opacity: 1,
      y: 0,
      duration: 0.35,
      ease: 'power2.out',
      delay: 0.2,
      onComplete: () => {
        this.#IS_ANIMATING = false;
      },
    });
  }

  #hideCenterCardInfo(card: HTMLElement): void {
    const info: Element | null = card.querySelector('.hero-card-info');

    if (!info) {
      return;
    }

    gsap.to(info, {
      opacity: 0,
      y: 16,
      duration: 0.25,
      ease: 'power2.in',
    });
  }

  #saveCardStates(): void {
    if (this.#LEFT_CARD) {
      const computed = gsap.getProperty(this.#LEFT_CARD, 'y') as number;
      const rotation = gsap.getProperty(this.#LEFT_CARD, 'rotation') as number;
      this.#cardStates.left = { y: computed, rotation };
    }

    if (this.#CENTER_CARD) {
      const computed = gsap.getProperty(this.#CENTER_CARD, 'y') as number;
      const rotation = gsap.getProperty(this.#CENTER_CARD, 'rotation') as number;
      this.#cardStates.center = { y: computed, rotation };
    }

    if (this.#RIGHT_CARD) {
      const computed = gsap.getProperty(this.#RIGHT_CARD, 'y') as number;
      const rotation = gsap.getProperty(this.#RIGHT_CARD, 'rotation') as number;
      this.#cardStates.right = { y: computed, rotation };
    }
  }

  #addFloatingAnimation(): void {
    if (!this.#LEFT_CARD || !this.#CENTER_CARD || !this.#RIGHT_CARD) {
      return;
    }

    this.#stopFloatingAnimation();

    gsap.set(this.#LEFT_CARD, {
      y: this.#cardStates.left.y,
      rotation: this.#cardStates.left.rotation,
    });
    gsap.set(this.#CENTER_CARD, {
      y: this.#cardStates.center.y,
      rotation: this.#cardStates.center.rotation,
    });
    gsap.set(this.#RIGHT_CARD, {
      y: this.#cardStates.right.y,
      rotation: this.#cardStates.right.rotation,
    });

    const leftTl: GSAPTimeline = gsap.timeline({ repeat: -1, yoyo: true });
    leftTl.to(this.#LEFT_CARD, {
      y: -10,
      rotation: 1,
      duration: 3,
      ease: 'sine.inOut',
    });
    this.#floatingTimelines.push(leftTl);

    const centerTl: GSAPTimeline = gsap.timeline({ repeat: -1, yoyo: true, delay: 0.5 });
    centerTl.to(this.#CENTER_CARD, {
      y: -15,
      rotation: -0.5,
      duration: 4,
      ease: 'sine.inOut',
    });
    this.#floatingTimelines.push(centerTl);

    const rightTl: GSAPTimeline = gsap.timeline({ repeat: -1, yoyo: true, delay: 1 });
    rightTl.to(this.#RIGHT_CARD, {
      y: -12,
      rotation: -1,
      duration: 3.5,
      ease: 'sine.inOut',
    });
    this.#floatingTimelines.push(rightTl);
  }

  #stopFloatingAnimation(): void {
    this.#floatingTimelines.forEach((tl: GSAPTimeline): void => {
      tl.kill();
    });
    this.#floatingTimelines = [];
  }

  #handleCardClick = (e: Event): void => {
    e.preventDefault();
    const card = e.currentTarget as HTMLElement;
    this.swapCards(card);
  };
}
